/**
 * Local socket watchdog — pure decision logic.
 *
 * Why this exists: the Events API closes every connection at 24 h. Usually Amplify opens a new
 * socket ~5 s later, but in a few percent of cap events it does not, and the tablet stays deaf
 * for hours while the Hub says nothing actionable:
 *
 *  - H1: a stale 15 s `checkAckOk` timer from a superseded handshake records CONNECTION_FAILED
 *    against a healthy socket. The Hub then reads `Disconnected` while frames still arrive, and
 *    when the server finally closes the socket the repeated `Disconnected` is dropped by
 *    Amplify's de-duplication: no transition, so reconnection never starts.
 *  - H2: OFFLINE recorded, network monitoring disabled before ONLINE → `ConnectedPendingNetwork`
 *    then `ConnectionDisruptedPendingNetwork`, which HALT the reconnection and never leave.
 *
 * Detection driven by Hub transitions cannot see either, so the watchdog reads the socket itself
 * (see `getSocketProbe` in eventLedger.js): it costs no request.
 *
 * HARD CONSTRAINT: nothing here derives or touches `isOnline`. A fleet-wide false-offline outage
 * once came from gating connectivity on socket state (see `computeIsOnline` in eventLedger.js).
 * `onLine` below is `navigator.onLine`, passed in by the caller, and only ever DISABLES a rule.
 *
 * The decisions are pure functions of a probe + the clock so the table is unit-testable without a
 * socket; App.vue owns the timer, the recovery and the telemetry.
 */

/** How often App.vue evaluates the rules. Local only. */
export const WATCHDOG_TICK_MS = 30000;

/** DEAD: the socket has not been open-and-ready for this long... */
export const DEAD_AFTER_MS = 60000;
/**
 * ...AND the Hub has not changed state for this long. H1 and H2 are silent: after the close the Hub
 * says nothing more. An Amplify that is still retrying is not: it cycles Connecting /
 * ConnectionDisrupted every minute or so, and a closed socket during that loop is Amplify at work,
 * not a halt. After an outage that takes a whole fleet's sockets down at once, acting in the middle
 * of every tablet's own retry loop would turn one outage into a request storm.
 */
export const DEAD_HUB_SILENT_MS = 90000;
/**
 * ZOMBIE: the socket is open but no frame (`ka` or data) for this long. AppSync Events sends a
 * `ka` about every 60 s, so 150 s is two and a half missed intervals; Amplify itself only alerts
 * at 65 s and gives up at 5 min.
 */
export const ZOMBIE_KA_AGE_MS = 150000;
/** AGED: one socket object open past the Events API's 24 h cap plus 3 minutes of grace. */
export const AGED_AFTER_MS = 24 * 60 * 60 * 1000 + 3 * 60 * 1000;
/** BOMB: a healthy-looking socket under a Hub state that halts Amplify's reconnection. */
export const BOMB_AFTER_MS = 120000;
/** Hub states that leave Amplify's ReconnectionMonitor halted (HALT_RECONNECT) or waiting forever. */
export const BOMB_HUB_STATES = Object.freeze([
  'Disconnected',
  'ConnectedPendingDisconnect',
  'ConnectionDisruptedPendingNetwork',
  'ConnectedPendingNetwork',
]);

/** Backoff between recoveries within one episode: 30 s, doubling, capped at 10 min (each step ±25%). */
export const RECOVERY_MIN_INTERVAL_MS = 30000;
export const RECOVERY_MAX_INTERVAL_MS = 600000;
export const BACKOFF_JITTER = 0.25;
/** The first action of an episode waits a random 0-60 s so a fleet that lost its sockets together does not act together. */
export const FIRST_ACTION_JITTER_MAX_MS = 60000;
/** A socket rebuild that has not finished in this long is given up on (a hung connect would hold every recovery for ever). */
export const REBUILD_TIMEOUT_MS = 90000;
/** When the interface or the foreground returns and the socket is dead, the rebuild waits a random 0-10 s: a whole site's Wi-Fi coming back must not rebuild every tablet in the same second. */
export const ONLINE_RETURN_JITTER_MAX_MS = 10000;
/**
 * ZOMBIE is ignored this long after the app resumes (visible again, or a timer gap that says the
 * device slept): a keep-alive that arrived while the tablet slept is not coming, and AppSync's next
 * one is up to 60 s away, so the age on the socket is stale, not dead.
 */
export const ZOMBIE_RESUME_HOLDOFF_MS = 75000;
/** Watchdog recoveries per tablet per local day. A tablet whose socket cannot be revived must not loop. */
export const DAILY_RECOVERY_CAP = 12;

// Private Amplify numbers (api-graphql 4.8.8). Pinned by tests/unit/socketWatchdog.amplify.spec.js,
// which runs the real provider and fails when either constant moves.
export const WS_OPEN = 1; // WebSocket.OPEN
export const SOCKET_STATUS_READY = 1; // Amplify SOCKET_STATUS.READY
export const SOCKET_STATUS_CLOSED = 0; // Amplify SOCKET_STATUS.CLOSED

/** Amplify's provider holds a live, ready socket. */
export function isSocketOpen(probe) {
  return Boolean(probe)
    && probe.hasProvider === true
    && probe.wsReadyState === WS_OPEN
    && probe.socketStatus === SOCKET_STATUS_READY;
}

/**
 * DEAD per the probe: not open-and-ready for at least DEAD_AFTER_MS, and no Hub transition for at
 * least DEAD_HUB_SILENT_MS (an active Amplify retry is not a halt).
 */
export function isProbeDead(probe, now) {
  if (!probe || probe.hasProvider !== true || isSocketOpen(probe)) return false;
  if (probe.notReadySince == null || now - probe.notReadySince < DEAD_AFTER_MS) return false;
  return probe.hubStateSince == null || now - probe.hubStateSince >= DEAD_HUB_SILENT_MS;
}

/**
 * Which rule, if any, says the socket must be rebuilt. First match wins, in the order the rules
 * are cheapest to be sure of: DEAD, ZOMBIE, AGED, BOMB.
 *
 * Every rule needs registered channels and `navigator.onLine === true`: nothing is recoverable
 * before the restaurant is known, and reconnecting while the OS interface is down is futile (the
 * `online` listener handles the return).
 *
 * ZOMBIE alone is also held off while the app is resuming (`zombieHoldoffUntil`, see resumePlan).
 *
 * BOMB alone waits for a quiet moment (`quiet`): its socket still delivers, so there is no
 * hurry, and the rebuild refetches the orders and rebuilds the list under the operator's hands.
 *
 * @returns {{rule: 'dead'|'zombie'|'aged'|'bomb', confirmMs: number}|null}
 */
export function evaluateSocketRules({ probe, now, onLine, channelCount, quiet = true, zombieHoldoffUntil = null }) {
  if (!probe || probe.hasProvider !== true) return null;
  if (!(channelCount > 0) || onLine !== true) return null;

  if (isProbeDead(probe, now)) return { rule: 'dead', confirmMs: DEAD_AFTER_MS };
  if (!isSocketOpen(probe)) return null; // closed or reconnecting, but not for long enough yet

  const resuming = zombieHoldoffUntil != null && now < zombieHoldoffUntil;
  if (!resuming && probe.kaAgeMs != null && probe.kaAgeMs > ZOMBIE_KA_AGE_MS) {
    return { rule: 'zombie', confirmMs: ZOMBIE_KA_AGE_MS };
  }
  if (probe.socketOpenedAt != null && now - probe.socketOpenedAt > AGED_AFTER_MS) {
    return { rule: 'aged', confirmMs: AGED_AFTER_MS };
  }
  if (
    BOMB_HUB_STATES.includes(probe.hubState)
    && probe.hubStateSince != null
    && now - probe.hubStateSince >= BOMB_AFTER_MS
    && quiet
  ) {
    return { rule: 'bomb', confirmMs: BOMB_AFTER_MS };
  }
  return null;
}

/** Wait before the next recovery given how many have already run in this episode. */
export function recoveryBackoffMs(attempts) {
  return Math.min(RECOVERY_MIN_INTERVAL_MS * Math.pow(2, Math.max(0, attempts)), RECOVERY_MAX_INTERVAL_MS);
}

/** The same step with ±25% jitter: tablets that failed together do not retry together. */
export function jitteredBackoffMs(attempts, random = Math.random) {
  return Math.round(recoveryBackoffMs(attempts) * (1 - BACKOFF_JITTER + 2 * BACKOFF_JITTER * random()));
}

/**
 * What a watchdog tick does about resuming. A hidden page, or a tick that comes much later than the
 * timer says (the device slept, the page was frozen), has a stale keep-alive age rather than a dead
 * socket: the tick is skipped while hidden and ZOMBIE is held off for a while after.
 *
 * @returns {{skip: boolean, zombieHoldoffUntil: number|null}}
 */
export function resumePlan({ hidden, gapMs, now, zombieHoldoffUntil }) {
  if (hidden) return { skip: true, zombieHoldoffUntil: now + ZOMBIE_RESUME_HOLDOFF_MS };
  if (gapMs > 2 * WATCHDOG_TICK_MS) return { skip: false, zombieHoldoffUntil: now + ZOMBIE_RESUME_HOLDOFF_MS };
  return { skip: false, zombieHoldoffUntil };
}

/**
 * The backoff bookkeeping of one recovery attempt. A rebuild that is NOT a deaf-episode attempt (a
 * manual duplicate on a socket that looked healthy) touches nothing: otherwise a few duplicates would
 * leave a real DEAD waiting 8-10 minutes for its first rebuild.
 *
 * @returns {{attempts: number, nextAt: number|null}}  `nextAt` null = leave the schedule as it was
 */
export function planRecoveryAttempt({ attempts, now, random = Math.random, countsTowardBackoff = true }) {
  if (!countsTowardBackoff) return { attempts, nextAt: null };
  const done = attempts + 1;
  // 30 s, then doubling to 10 min, each step +-25%.
  return { attempts: done, nextAt: now + jitteredBackoffMs(done - 1, random) };
}

/** How long a dead socket's rebuild waits when the interface or the foreground comes back, 0 to 10 s. */
export function onlineReturnDelayMs(random = Math.random) {
  return Math.round(ONLINE_RETURN_JITTER_MAX_MS * random());
}

/**
 * The decision behind "back online / back in the foreground and the socket is dead": wait a random
 * 0-10 s, look again (Amplify may have got there first, or the session may have ended), and spend
 * one of the day's recoveries. Dependencies are injected so the whole sequence is unit-testable.
 *
 * `throttled()` says a rebuild ran moments ago (the shared backoff): nothing is spent then.
 *
 * @returns {Promise<{action: 'none'|'offline'|'cancelled'|'recovered_meanwhile'|'throttled'|'capped'|'rebuild', probe: object|null}>}
 */
export async function planOnlineReturnRebuild({
  getProbe, now = Date.now, sleep, random = Math.random, spend, isCancelled = () => false, throttled = () => false,
  onLine = () => true,
}) {
  const first = getProbe();
  if (!isProbeDead(first, now())) return { action: 'none', probe: first };
  // Foreground while the interface is down: a rebuild is futile and would spend the day's cap. The
  // `online` event is what brings it back.
  if (onLine() !== true) return { action: 'offline', probe: first };
  await sleep(onlineReturnDelayMs(random));
  if (isCancelled()) return { action: 'cancelled', probe: null };
  if (onLine() !== true) return { action: 'offline', probe: null };
  const again = getProbe();
  if (!isProbeDead(again, now())) return { action: 'recovered_meanwhile', probe: again };
  if (throttled()) return { action: 'throttled', probe: again };
  if (!spend(now())) return { action: 'capped', probe: again };
  return { action: 'rebuild', probe: again };
}

/**
 * `connect()` for a channel, but never keep it when the session ended while it was connecting: a
 * sign-out or an expired session during the connect must leave no subscription behind.
 *
 * @returns the channel, or null when it was stale (and has been closed)
 */
export async function connectUnlessStale(connect, isStale) {
  const channel = await connect();
  if (!isStale()) return channel;
  try { await channel.close(); } catch (_) { /* nothing left to close */ }
  return null;
}

/** How long the FIRST action of an episode waits, 0 to 60 s. */
export function firstActionDelayMs(random = Math.random) {
  return Math.round(FIRST_ACTION_JITTER_MAX_MS * random());
}

// ── daily budget ────────────────────────────────────────────────────────────────────────────

const BUDGET_KEY = 'bronze_socket_watchdog_budget';

/** The tablet's local calendar day, `YYYY-MM-DD`. */
export function localDay(now) {
  const d = new Date(now);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function readBudget(now) {
  try {
    const parsed = JSON.parse(localStorage.getItem(BUDGET_KEY) || 'null');
    if (parsed && parsed.day === localDay(now) && Number.isFinite(parsed.count)) return parsed;
  } catch (_) { /* unreadable storage: start the day afresh */ }
  return { day: localDay(now), count: 0 };
}

/** Recoveries still available today. Persisted, so a reload does not reset the cap. */
export function recoveriesLeftToday(now) {
  return Math.max(0, DAILY_RECOVERY_CAP - readBudget(now).count);
}

/** Spend one recovery. Returns false (and spends nothing) once the day's cap is reached. */
export function spendRecovery(now) {
  const budget = readBudget(now);
  if (budget.count >= DAILY_RECOVERY_CAP) return false;
  budget.count += 1;
  try {
    localStorage.setItem(BUDGET_KEY, JSON.stringify(budget));
  } catch (_) { /* a full disk must not stop a recovery */ }
  return true;
}

// ── session_health ──────────────────────────────────────────────────────────────────────────

const seconds = (ms) => (ms == null ? null : Math.round(ms / 1000));

/**
 * The socket fields `session_health` carries. Read from the local probe: no request, and null
 * before the first Hub event (the provider reference arrives with it).
 */
export function socketHealthFields(probe, now) {
  if (!probe || probe.hasProvider !== true) {
    return { socket_state: null, socket_state_age_s: null, ws_ready_state: null, ka_age_s: null, socket_age_min: null };
  }
  return {
    socket_state: probe.hubState ?? null,
    socket_state_age_s: probe.hubStateSince == null ? null : seconds(now - probe.hubStateSince),
    ws_ready_state: probe.wsReadyState ?? null,
    ka_age_s: seconds(probe.kaAgeMs),
    socket_age_min: probe.socketOpenedAt == null ? null : Math.round((now - probe.socketOpenedAt) / 60000),
  };
}
